import { useState } from 'react';

import useApplication from 'hooks/useApplication';
import usePlannerFetch from 'hooks/usePlannerFetch';
import useRetry from 'hooks/useRetry';

import {
    COVERAGE_AREA_BOUNDARY_ONLY,
    COVERAGE_AREA_ROUTE_AND_BOUNDARY,
    EXPORT_FAILED,
    EXPORT_PATH_SUFFIX,
    GEOJSON_LINE_STRING_TYPE,
    GEOJSON_POINT_TYPE,
    ROUTE_PATTERN_EDGE_SWEEP,
    ROUTE_PATTERN_EDGE_SWEEP_ONLY,
    ROUTE_PATTERN_NO_EDGE_SWEEP,
    SESSIONS_API
} from './constants';
import type {
    ApplicationSettingsUpdateRequest,
    ApplicationUpdateRequest,
    ExportedPlan,
    PlanWaypoint,
    ResolvedRoutePlanConfig,
    SavePlan,
    SpraySettings,
    UseSavePlanReturn
} from './types';

type GeoJsonPoint = { type: 'Point'; coordinates: [number, number] };
type GeoJsonLineString = { type: 'LineString'; coordinates: [number, number][] };

const buildFlightPath = ({
    coordinates
}: {
    coordinates: [number, number][];
}): GeoJsonLineString => ({
    coordinates,
    type: GEOJSON_LINE_STRING_TYPE
});

const buildPoint = ({
    latitude,
    longitude
}: {
    latitude: number;
    longitude: number;
}): GeoJsonPoint => ({
    coordinates: [longitude, latitude],
    type: GEOJSON_POINT_TYPE
});

const deriveRoutePattern = ({ config }: { config: ResolvedRoutePlanConfig }) => {
    if (config.coverageArea === COVERAGE_AREA_BOUNDARY_ONLY) {
        return ROUTE_PATTERN_EDGE_SWEEP_ONLY;
    }

    if (config.coverageArea === COVERAGE_AREA_ROUTE_AND_BOUNDARY) {
        return ROUTE_PATTERN_EDGE_SWEEP;
    }

    if (config.headlandPasses > 0) {
        return ROUTE_PATTERN_EDGE_SWEEP;
    }

    return ROUTE_PATTERN_NO_EDGE_SWEEP;
};

const mapWaypoints = ({ waypoints }: { waypoints: PlanWaypoint[] }) =>
    waypoints.map((waypoint, index) => ({
        altitudeMeters: waypoint.altitudeAgl,
        isComplete: false,
        isPumpOn: waypoint.isPumpOn,
        location: buildPoint({ latitude: waypoint.lat, longitude: waypoint.lon }),
        waypointIndex: index
    }));

const buildSettings = ({
    exportedPlan,
    spraySettings
}: {
    exportedPlan: ExportedPlan;
    spraySettings: SpraySettings;
}): ApplicationSettingsUpdateRequest => {
    const { config, gcsCompat, result } = exportedPlan;
    const routePattern = deriveRoutePattern({ config });
    const waypoints = mapWaypoints({ waypoints: result.waypoints });

    // TODO(schema-mismatch): generated Point/LineString types are JTS-shaped but the
    // backend accepts GeoJSON. `pages/MapTest/index.tsx` uses the same cast. Resolve
    // by regenerating Orval against a spec that declares GeoJSON for flightPath and
    // WaypointCreateRequest.location. Tracked as an open question in PRD §13.
    return {
        altitudeRelativeToTakeoffMeters: gcsCompat.gcsParams.altitudeRelativeToTakeoffMeters,
        applicationType: spraySettings.applicationType,
        distanceFromBoundariesMeters: gcsCompat.gcsParams.distanceFromBoundariesMeters,
        distanceFromObstaclesMeters: gcsCompat.gcsParams.distanceFromObstaclesMeters,
        flightMode: gcsCompat.gcsParams.flightMode,
        flightPath: buildFlightPath({ coordinates: gcsCompat.flightPath.coordinates }),
        nozzleRatePercentage: spraySettings.nozzleRatePercentage,
        overlapMeters: spraySettings.overlapMeters,
        pathRotationDegrees: gcsCompat.gcsParams.pathRotationDegrees,
        productCategory: spraySettings.productCategory,
        rateKilogramsPerHectare: spraySettings.rateKilogramsPerHectare,
        rateLitersPerHectare: spraySettings.rateLitersPerHectare,
        routePattern,
        routeWidthMeters: gcsCompat.gcsParams.routeWidthMeters,
        speedMetersPerSecond: spraySettings.speedMetersPerSecond,
        turntablePercentage: spraySettings.turntablePercentage,
        waypoints
    } as ApplicationSettingsUpdateRequest;
};

const useSavePlan = (): UseSavePlanReturn => {
    const [isSaving, setIsSaving] = useState(false);
    const { plannerPost } = usePlannerFetch();
    const { fetchApplication, updateApplication } = useApplication();
    const { withRetry } = useRetry();

    const fetchExportedPlan = async ({
        sessionId
    }: {
        sessionId: string;
    }): Promise<ExportedPlan | null> => {
        const exportUrl = `${SESSIONS_API}/${sessionId}${EXPORT_PATH_SUFFIX}`;

        return await withRetry(
            () => plannerPost<ExportedPlan>(exportUrl, JSON.stringify({})),
            EXPORT_FAILED,
            () => fetchExportedPlan({ sessionId })
        );
    };

    const savePlan: SavePlan = async ({ applicationId, sessionId, spraySettings }) => {
        try {
            setIsSaving(true);

            const exportedPlan = await fetchExportedPlan({ sessionId });

            if (!exportedPlan) {
                return null;
            }

            const existingApplication = await fetchApplication({ id: applicationId });

            if (!existingApplication) {
                return null;
            }

            const applicationSettings = buildSettings({ exportedPlan, spraySettings });

            const updateRequest: ApplicationUpdateRequest = {
                applicationSettings,
                batteryIds: existingApplication.batteries.map(battery => battery.id),
                boundaryId: existingApplication.boundaryId,
                droneIds: existingApplication.drones.map(drone => drone.id),
                name: existingApplication.name ?? '',
                scheduledPilotIds: existingApplication.scheduledPilots.map(pilot => pilot.id),
                siteId: existingApplication.site?.id,
                status: existingApplication.status
            };

            return await updateApplication({
                application: updateRequest,
                applicationId
            });
        } finally {
            setIsSaving(false);
        }
    };

    return {
        isSaving,
        savePlan
    };
};

export default useSavePlan;
